import React, { useEffect } from "react";
import "./PrivacyPolicyPage.css";

const thirdPartyProcessors = [
  {
    name: "OpenAI",
    purpose: "AI generation, realtime voice sessions, transcription, document digestion, and recording analysis.",
  },
  {
    name: "Stripe",
    purpose: "Subscription billing, checkout, customer records, payment operations, and billing portal services.",
  },
  {
    name: "RevenueCat",
    purpose: "Mobile subscription management, app store purchase history, entitlement status, and subscription analytics.",
  },
  {
    name: "Google",
    purpose: "Google sign-in and identity verification for users who choose Google authentication.",
  },
  {
    name: "Apple",
    purpose: "Apple sign-in and identity verification for users who choose Apple authentication.",
  },
  {
    name: "Microsoft",
    purpose: "Microsoft sign-in, Microsoft-based SSO flows, and Microsoft-powered transactional email delivery.",
  },
  {
    name: "Expo",
    purpose: "Delivery of mobile push notifications through Apple and Google notification services.",
  },
  {
    name: "Hosting, storage, and database providers",
    purpose: "Application hosting, persistent storage, backups, networking, and database infrastructure.",
  },
];

export default function PrivacyPolicyPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <main className="terms-container">
      <section className="terms-card">
        <h1 className="terms-title">PRIVACY POLICY</h1>
        <p><strong>Effective Date:</strong> October 9, 2026</p>
        <p>
          This Privacy Policy explains how Telome LLC ("Telome," "we," "us," or "our")
          collects, uses, stores, shares, and protects information when you use our website,
          mobile application, beta products, practice sessions, training sessions, business
          features, and related services (collectively, the "Services").
        </p>
        <p>
          We aim to be transparent. This policy is written to reflect how the Services currently
          operate based on our product and codebase, including account authentication, live AI
          voice sessions, recordings, transcripts, document processing, billing, and organization
          features.
        </p>

        <h2>1. Information We Collect</h2>
        <p>We may collect the following categories of information:</p>
        <ul>
          <li>
            <strong>Account and profile information:</strong> name, email address, password hash,
            authentication provider, provider account identifiers, account creation date, and
            profile details.
          </li>
          <li>
            <strong>Authentication and session information:</strong> access tokens, refresh tokens,
            login state, authentication cookies for web users, mobile auth tokens stored on device,
            password reset requests and verification data, and related security metadata.
          </li>
          <li>
            <strong>User-submitted content:</strong> prompts, meeting setup inputs, goals, custom
            goals, notes, additional context, uploaded file metadata, document-derived context,
            training preferences, feedback inputs, and other content you choose to submit.
          </li>
          <li>
            <strong>Conversation and transcript data:</strong> conversation turns, transcripts,
            transcript excerpts, summaries, feedback, highlight snippets, recommendations, and
            training plans generated or stored in connection with your sessions.
          </li>
          <li>
            <strong>Audio and recording data:</strong> microphone input used in live sessions,
            session recordings, uploaded recording files, recording duration and file metadata,
            timestamps, transcription data, pacing metrics, filler-word metrics, and
            recording-derived coaching output.
          </li>
          <li>
            <strong>Document data:</strong> document names, file types, sizes, document-derived
            summaries, and document context used to personalize practice or training sessions.
          </li>
          <li>
            <strong>Billing and subscription information:</strong> billing account information,
            Stripe customer identifiers, subscription identifiers, plan details, billing status,
            entitlement usage, and seat counts. We do not store full payment card numbers in our
            application databases.
          </li>
          <li>
            <strong>Organization and business information:</strong> organization membership, roles,
            group memberships, assignments, invite details, SSO tenant associations, and business
            demo request information such as work email, job title, phone number, company, country,
            and message content.
          </li>
          <li>
            <strong>Technical, device, and security information:</strong> request metadata, request
            identifiers, route/path information, origin or host information, error logs, rate-limit
            and abuse-prevention signals, general service usage records, mobile installation and push
            tokens, notification permission state, timezone, and recent account activity timestamps.
          </li>
        </ul>

        <h2>2. How We Collect Information</h2>
        <ul>
          <li>Directly from you when you create an account, log in, fill out forms, request a demo, set up meetings, upload documents, or use our Services.</li>
          <li>Automatically when you use the Services, including through cookies, local storage, secure device storage, logs, and usage-related records.</li>
          <li>From identity providers when you choose to sign in through Google, Apple, or Microsoft.</li>
          <li>From payment and billing providers when you purchase or manage a subscription.</li>
          <li>From your organization or team administrators when your account is provisioned, assigned, invited, or managed through organization features.</li>
        </ul>

        <h2>3. How We Use Information</h2>
        <ul>
          <li>Provide, operate, maintain, secure, and improve the Services.</li>
          <li>Authenticate users and manage accounts, sessions, and password resets.</li>
          <li>Generate AI conversations, training sessions, transcripts, summaries, feedback, coaching, and recommendations.</li>
          <li>Process and analyze recordings and uploaded materials to support product functionality.</li>
          <li>Create and manage subscriptions, billing records, entitlements, and customer support flows.</li>
          <li>Operate organization, assignment, invite, and team-management features.</li>
          <li>Detect misuse, enforce our terms, investigate incidents, and comply with legal obligations.</li>
          <li>Debug, test, monitor, and improve system performance, reliability, and safety.</li>
          <li>Send optional, timezone-aware notifications based on account inactivity and your notification preferences.</li>
          <li>
            Contact you to request voluntary feedback or invite you to participate in product
            research so we can understand and improve the Services.
          </li>
          <li>Communicate with you about authentication, account activity, invites, billing, product updates, and support issues.</li>
        </ul>

        <h2>4. AI, Audio, Transcript, and Recording Processing</h2>
        <p>
          Telome uses AI-powered services to enable practice conversations, realtime voice
          sessions, transcription, document digestion, feedback generation, and recording
          analysis.
        </p>
        <p>
          When you use live voice features, microphone audio, session instructions, and related
          meeting context may be transmitted to our AI providers to support realtime interaction,
          transcription, and response generation. When you upload or create recordings, we may
          store the recordings, transcribe them, analyze them, generate transcript-based insights,
          and return summaries, highlights, pacing metrics, filler-word analysis, and training
          recommendations.
        </p>
        <p>
          We also store meeting conversation history and transcripts in support of product
          functionality, meeting review, and feedback generation. Certain stored conversation
          content is encrypted at rest in our databases, but we do not represent that all data is
          encrypted end-to-end.
        </p>

        <h2>5. Document Uploads and Derived Context</h2>
        <p>
          If you upload documents or other materials, we may process their contents to generate
          document context or summaries that are used to personalize your sessions. We may store
          document metadata and derived context. Depending on the workflow, we may not retain every
          original uploaded file permanently, but we may retain derived content, metadata, and
          outputs associated with those materials.
        </p>

        <h2>6. Cookies, Local Storage, and Mobile Device Storage</h2>
        <p>
          On the web, we use authentication cookies and related session mechanisms to keep you
          signed in and secure your account. On mobile, authentication tokens may be stored in
          device-secure storage and, in some limited environments, fallback storage mechanisms may
          be used if secure storage is unavailable. We may also use browser or device storage for
          session continuity, pending flows, and reliability-related features.
        </p>

        <h2>7. Organization and Team Accounts</h2>
        <p>
          If you use Telome through an employer, school, customer organization, or team account,
          your organization may be able to view, manage, assign, or receive certain information
          relating to your account, organization membership, group membership, assigned sessions,
          training activity, and related service usage. In those contexts, your organization may
          act as a controller or business customer for certain information, and Telome may act as a
          service provider or processor on that organization&apos;s behalf.
        </p>

        <h2>8. Third-Party Processors</h2>
        <p>
          We work with third-party service providers and processors to operate the Services. These
          providers may process information on our behalf for the purposes described in this policy.
        </p>
        <ul>
          {thirdPartyProcessors.map((processor) => (
            <li key={processor.name}>
              <strong>{processor.name}:</strong> {processor.purpose}
            </li>
          ))}
        </ul>

        <h2>9. Payment Data and Purchase History</h2>
        <p>
          Telome uses RevenueCat to help manage mobile trials, paid subscriptions, app store
          purchase history, subscription status, and access to paid features. RevenueCat may
          process app user identifiers, product identifiers, entitlement status, purchase events,
          renewal and cancellation events, and related subscription metadata. RevenueCat&apos;s
          Privacy Policy is available at{" "}
          <a href="https://www.revenuecat.com/privacy/" rel="noreferrer" target="_blank">
            https://www.revenuecat.com/privacy/
          </a>
          .
        </p>
        <p>
          Telome uses Stripe to process web subscription checkout, billing records, customer
          records, payment status, invoices, subscription changes, cancellations, and billing
          portal services. Stripe may process payment and billing information needed to complete
          those transactions. Telome does not store full payment card numbers in our application
          databases. Stripe&apos;s Privacy Policy is available at{" "}
          <a href="https://stripe.com/privacy" rel="noreferrer" target="_blank">
            https://stripe.com/privacy
          </a>
          .
        </p>

        <h2>10. How We Share Information</h2>
        <ul>
          <li>With service providers and processors that help us operate the Services.</li>
          <li>With authentication, billing, email, hosting, storage, infrastructure, and AI providers as needed to deliver the Services.</li>
          <li>With your organization or team administrators where your account is organization-managed or uses shared organization features.</li>
          <li>With professional advisors, auditors, insurers, acquirers, or counterparties in connection with corporate transactions or legal matters.</li>
          <li>When required by law, subpoena, court order, regulatory process, or to protect rights, safety, users, Telome, or the public.</li>
        </ul>
        <p>
          We do not sell personal information for third-party marketing. We also do not describe
          our practices as "never sharing data with third parties" because we do use third-party
          processors to operate the Services.
        </p>

        <h2>11. Data Retention</h2>
        <p>
          We retain information for as long as reasonably necessary to provide the Services,
          operate accounts, support recordings and session history, maintain security, comply with
          legal obligations, resolve disputes, and enforce agreements. Different categories of data
          may be retained for different periods. For example, short-lived authentication or reset
          tokens may be retained only for their limited validity periods, while account, meeting,
          transcript, recording, billing, and audit-related data may persist longer.
        </p>
        <p>
          If you delete your account, we may delete or de-identify certain information, subject to
          legal, billing, fraud-prevention, dispute-resolution, security, backup, and operational
          exceptions. We do not promise that all information is deleted immediately upon request.
        </p>

        <h2>12. Security</h2>
        <p>
          We use administrative, technical, and organizational safeguards designed to protect
          information, which may include hashing, encryption for certain stored data, access
          controls, secured communications, and operational monitoring. No system is completely
          secure, and we cannot guarantee absolute security.
        </p>

        <h2>13. Your Rights and Choices</h2>
        <p>
          Depending on where you live, you may have rights to access, know, correct, delete, or
          obtain a copy of certain personal information, or to limit or object to certain
          processing. California residents may have rights under the CCPA/CPRA, including rights to
          know, delete, correct, and receive equal treatment for exercising their privacy rights.
        </p>
        <p>
          You may also choose not to provide certain information, but some Services may not function
          properly without it. If you use an organization-managed account, some privacy requests may
          need to be directed to your organization.
        </p>
        <p>
          Participation in feedback requests and product research is voluntary. You may decline a
          request or ask us not to send you future feedback or research invitations without affecting
          your access to the Services.
        </p>

        <h2>14. International Processing</h2>
        <p>
          Your information may be processed or stored in the United States or other jurisdictions
          where we or our service providers operate. By using the Services, you understand that your
          information may be transferred to and processed in countries that may have different data
          protection laws than your home jurisdiction.
        </p>

        <h2>15. Children</h2>
        <p>
          The Services are not intended for children under the minimum age required by applicable
          law, and we do not knowingly collect personal information from children in violation of
          applicable law. If you believe a child has provided us personal information improperly,
          contact us so we can investigate and take appropriate action.
        </p>

        <h2>16. Changes to This Policy</h2>
        <p>
          We may update this Privacy Policy from time to time. If we make material changes, we may
          update the effective date, post notice in the Services, or provide another form of notice
          where appropriate. Continued use of the Services after an update may constitute acceptance
          of the revised policy to the extent permitted by law.
        </p>

        <h2>17. Contact</h2>
        <p>
          For privacy-related questions or requests, contact Telome LLC at support@telome.ai or
          any updated contact address we provide in the Services.
        </p>
      </section>
    </main>
  );
}
